import { Card, CardBody, CardFooter, Image } from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { useGlobalStore } from "@/stores/GlobalStore";
import { logger } from "@/telemetry";

export default function MenuCard() {
  const navigate = useNavigate();
  const { permissions, isLoading } = usePermissions();
  const { brand } = useBrand();
  const { sidebar } = useGlobalStore();
  const { isSidebarCompact } = sidebar;

  logger.info("MenuCard - Using secure permissions", {
    workflows: String(permissions.workflows),
    knowledgeHub: String(permissions.knowledge_hub),
    promptLibrary: String(permissions.prompt_library),
    monitoring: String(permissions.monitoring),
    settings: String(permissions.settings),
  });
  const { t } = useTranslation();

  // Build list based on secure permissions
  const list = [
    // Add dashboard as first item if configured to show in sidebar
    ...(brand.dashboard?.showInSideBar && brand.dashboard?.url
      ? [
          {
            name: brand.dashboard.title ? t(brand.dashboard.title) : t("Dashboard"),
            title: brand.dashboard.subtitle ? t(brand.dashboard.subtitle) : t("Dashboard"),
            img: `/brands/${brand.prefix || "webuild"}/dashboard.svg`,
            link: `/${brand.dashboard?.urlSegment ?? "dashboard"}`,
            dataID: "dashboard",
          },
        ]
      : []),
    {
      name: t(brand.menuItems?.Pathfinder?.name || "Chat"),
      title: t("Chat"),
      img: `/brands/${brand.prefix || "webuild"}/pathfinder.svg`,
      link: "/chat",
      dataID: "chat",
    },
    ...(permissions.workflows || brand.menuItems?.["Workflows"]
      ? [
          {
            name: t(brand.menuItems?.Workflow?.name || "Workflows"),
            title: t("Accelerators across common AI use cases"),
            img: `/brands/${brand.prefix || "webuild"}/playbook.svg`,
            link: "/workflows",
            dataID: "workflows",
          },
        ]
      : []),
    // Knowledge Hub - check secure permission
    ...(permissions.knowledge_hub
      ? [
          {
            name: t(brand.menuItems?.["Knowledge Hub"]?.name || "Knowledge Hub"),
            title: t("Secure access to your data & knowledge"),
            img: `/brands/${brand.prefix || "webuild"}/knowledge.svg`,
            link: "/datahub",
            dataID: "data-hub",
          },
        ]
      : []),
    // Prompt Library - check secure permission
    ...(permissions.prompt_library
      ? [
          {
            name: t(brand.menuItems?.["Prompt Library"]?.name || "Prompt Library"),
            title: t("Design, craft and catalogue prompts"),
            img: `/brands/${brand.prefix || "webuild"}/prompt.svg`,
            link: "/prompts",
            dataID: "prompts",
          },
        ]
      : []),
    // Monitoring - check secure permission (uses solutions.svg icon)
    ...(permissions.monitoring
      ? [
          {
            name: brand.menuItems?.["Monitoring"]?.name || t("Monitoring"),
            title: t("Monitor AI usage and performance"),
            img: `/brands/${brand.prefix || "webuild"}/solutions.svg`,
            link: "/metrics",
            dataID: "monitoring",
          },
        ]
      : []),
    // Models Overview - always available (no permission required)
    {
      name: t("Models Overview"),
      title: t("View available AI models and capabilities"),
      img: `/brands/${brand.prefix || "webuild"}/models.svg`,
      link: "/models",
      dataID: "models-overview",
    },
    // Settings - check secure permission
    ...(permissions.settings
      ? [
          {
            name: brand.menuItems?.["Settings"]?.name || t("Settings"),
            title: t("Configure your AI platform"),
            img: `/brands/${brand.prefix || "webuild"}/settings.svg`,
            link: "/settings",
            dataID: "settings",
          },
        ]
      : []),
  ];

  // Show loading state if permissions are still loading
  if (isLoading) {
    return (
      <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 w-6/10">
        <div className="text-default-500">Loading permissions...</div>
      </div>
    );
  }

  return (
    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 w-6/10">
      {list.map((item) => (
        <Card
          key={item.title}
          className="py-4"
          data-id={item.dataID}
          isPressable={true}
          onPress={() => navigate(item.link)}
        >
          <CardBody className="flex justify-center items-center">
            <div className="w-full h-30 flex flex-col items-center justify-center gap-2">
              <Image
                classNames={
                  brand.name === "Invest Ai" ? { img: "invest-ai-icon-filter" } : undefined
                }
                height={brand.menuItemsIconSize || 100}
                radius="none"
                src={item.img}
              />
              <div
                className={`text-center text-foreground font-bold ${!isSidebarCompact ? "text-sm" : "text-xl"}`}
              >
                {item.name}
              </div>
            </div>
          </CardBody>
          {isSidebarCompact && (
            <CardFooter className="px-4 pb-4 pt-0 justify-center">
              <small className="text-default-600 text-center">{item.title}</small>
            </CardFooter>
          )}
        </Card>
      ))}
    </div>
  );
}
